import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger
} from "../ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Separator } from "../ui/separator";
import { Textarea } from "../ui/textarea";
import { useState, useCallback, useEffect, useMemo } from "react";
import CSVImportGeneralLeadModal from "../superadmin/CSVImportLeadModal";
import LeadEditModal from "../superadmin/LeadEditModal";
import {
  Plus,
  Search,
  Filter,
  FileText,
  Phone,
  Mail,
  MessageSquare,
  Edit,
  Calendar,
  ChevronRight,
  UserPlus,
  TrendingUp,
  Users,
  CheckCircle2,
  XCircle,
  Clock,
  ArrowUpRight,
  Download,
  SlidersHorizontal,
} from "lucide-react";

import API from "../api/api01";
import { cn } from "../ui/utils";
import { toast } from "sonner";
import { exportToCSV } from "../utils/exportUtils";

interface Lead {
  id: string;
  name: string;
  email: string;
  phone: string;
  course: string;
  source: "Website" | "Walk-in" | "Campaign" | "Referral";
  priority: "High" | "Medium" | "Low";
  counselor: {
    name: string;
    avatar: string;
  };
  lastFollowUp: string;
  stage: string;
  appliedDate: string;
  notes?: string;
}

interface Stage {
  id: string;
  name: string;
  color: string;
  leads: Lead[];
}

export function LeaveManagement() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCourse, setSelectedCourse] = useState("all");
  const [selectedCounselor, setSelectedCounselor] = useState("all");
  const [selectedSource, setSelectedSource] = useState("all");
  const [selectedPriority, setSelectedPriority] = useState("all");
  const [isAddLeadOpen, setIsAddLeadOpen] = useState(false);
  const [isBulkImportOpen, setIsBulkImportOpen] = useState(false);
  const [draggedLead, setDraggedLead] = useState<Lead | null>(null);
  const [isCallDialogOpen, setIsCallDialogOpen] = useState(false);
  const [isMessageDialogOpen, setIsMessageDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [messageText, setMessageText] = useState("");

  // Form states for new lead
  const [newLeadName, setNewLeadName] = useState("");
  const [newLeadPhone, setNewLeadPhone] = useState("");
  const [newLeadEmail, setNewLeadEmail] = useState("");
  const [newLeadCourse, setNewLeadCourse] = useState("");
  const [newLeadSource, setNewLeadSource] = useState<"Website" | "Walk-in" | "Campaign" | "Referral">("Website");
  const [newLeadPriority, setNewLeadPriority] = useState<"High" | "Medium" | "Low">("Medium");
  const [newLeadCounselor, setNewLeadCounselor] = useState("");
  const [newLeadNotes, setNewLeadNotes] = useState("");
  const [editingLead, setEditingLead] = useState<Lead | null>(null);


  const [stages, setStages] = useState<Stage[]>([
    { id: "new-enquiries", name: "New Enquiries", color: "bg-blue-500", leads: [] },
    { id: "contacted", name: "Contacted", color: "bg-indigo-500", leads: [] },
    { id: "application-submitted", name: "Application Submitted", color: "bg-purple-500", leads: [] },
    { id: "admission-offered", name: "Admission Offered", color: "bg-emerald-500", leads: [] },
    { id: "enrolled", name: "Enrolled", color: "bg-green-600", leads: [] },
  ]);

  const fetchLeads = useCallback(async () => {
    try {
      const response = await API.get("/leads/");
      // The API returns List[LeadStageList]
      if (response.data && Array.isArray(response.data)) {
        if (response.data.length > 0) {
          toast.success("Lead data updated");
          setStages(response.data.map((s: any) => ({
            id: s.id,
            name: s.name,
            color: getStageColor(s.id),
            leads: (s.leads || []).map((l: any) => ({
              id: l.id,
              name: l.name,
              email: l.email,
              phone: l.phone || "N/A",
              course: l.course || "N/A",
              source: l.source || "Website",
              priority: l.priority || "Medium",
              counselor: {
                name: l.counselor_name || "Unassigned",
                avatar: l.counselor_avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${l.name}`,
              },
              lastFollowUp: l.last_follow_up || "New",
              stage: l.lead_status,
              status: l.lead_status,
              appliedDate: l.applied_date ? new Date(l.applied_date).toLocaleDateString() : "Today",
            }))
          })));
        } else {
          console.warn("API returned empty stage list");
        }
      }
    } catch (err) {
      console.error("Failed to fetch leads", err);
      toast.error("Failed to load leads from server");
    }
  }, []);

  const getStageColor = (id: string) => {
    switch (id) {
      case "new-enquiries": return "bg-blue-500";
      case "contacted": return "bg-indigo-500";
      case "application-submitted": return "bg-purple-500";
      case "admission-offered": return "bg-emerald-500";
      case "enrolled": return "bg-green-600";
      default: return "bg-slate-500";
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const handleAddLead = async () => {
    if (!newLeadName || !newLeadEmail) {
      toast.error("Name and Email are required");
      return;
    }
    try {
      await API.post("/leads/", {
        name: newLeadName,
        email: newLeadEmail,
        phone: newLeadPhone,
        course: newLeadCourse,
        source: newLeadSource,
        priority: newLeadPriority,
        lead_status: "new-enquiries",
        notes: newLeadNotes,
        counselor_name: newLeadCounselor
      });
      toast.success("Lead added successfully!");
      setIsAddLeadOpen(false);
      // Added 800ms delay to ensure modal is fully closed before heavy Kanban re-render starts
      setTimeout(() => {
        fetchLeads();
      }, 800);
      // Reset form
      setNewLeadName(""); setNewLeadEmail(""); setNewLeadPhone("");
    } catch (err) {
      toast.error("Failed to create lead");
    }
  };

  const uniqueCounselors = useMemo(() => {
    const counselorNames = new Set<string>();
    stages.forEach(stage => {
      stage.leads.forEach(lead => {
        if (lead.counselor.name) counselorNames.add(lead.counselor.name);
      });
    });
    return Array.from(counselorNames);
  }, [stages]);
  const filteredStages = useMemo(() => {
    const searchLower = searchQuery.toLowerCase();
    const courseFilter = selectedCourse !== "all" ? selectedCourse.toLowerCase().replace(/[^a-z0-9]/g, '') : "";
    const sourceFilter = selectedSource.toLowerCase();

    return stages.map((stage) => ({
      ...stage,
      leads: stage.leads.filter((lead) => {
        const matchesSearch =
          lead.name.toLowerCase().includes(searchLower) ||
          lead.email.toLowerCase().includes(searchLower) ||
          lead.phone.includes(searchQuery);

        const matchesCourse =
          selectedCourse === "all" ||
          lead.course.toLowerCase().replace(/[^a-z0-9]/g, '').includes(courseFilter);

        const matchesSource =
          selectedSource === "all" ||
          lead.source.toLowerCase() === sourceFilter;

        const matchesPriority =
          selectedPriority === "all" ||
          lead.priority === selectedPriority;

        const matchesCounselor =
          selectedCounselor === "all" ||
          lead.counselor.name === selectedCounselor;

        return matchesSearch && matchesCourse && matchesSource && matchesPriority && matchesCounselor;
      }),
    }));
  }, [stages, searchQuery, selectedCourse, selectedSource, selectedPriority, selectedCounselor]);

  const handleExport = () => {
    const allLeads = stages.flatMap((s) => s.leads);
    const success = exportToCSV(allLeads, "leads_export.csv", {
      name: "Student Name",
      email: "Email",
      phone: "Phone Number",
      course: "Applied Course",
      source: "Lead Source",
      priority: "Priority",
      stage: "Status",
      appliedDate: "Applied On",
    });

    if (success) {
      toast.success("Lead data exported to CSV successfully!");
    } else {
      toast.error("Failed to export lead data.");
    }
  };

  const handleDragStart = (lead: Lead) => {
    setDraggedLead(lead);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (stageId: string) => {
    if (!draggedLead) return;

    try {
      await API.patch(`/leads/${draggedLead.id}/stage`, { lead_status: stageId });
      toast.success(`Moved ${draggedLead.name} to ${stageId.replace("-", " ")}`);
      fetchLeads();
    } catch (err) {
      toast.error("Failed to update lead stage");
    }
    setDraggedLead(null);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "High":
        return "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400";
      case "Medium":
        return "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400";
      case "Low":
        return "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400";
      default:
        return "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400";
    }
  };

  const getSourceColor = (source: string) => {
    switch (source) {
      case "Website":
        return "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400";
      case "Walk-in":
        return "bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-400";
      case "Campaign":
        return "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400";
      case "Referral":
        return "bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-400";
      default:
        return "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400";
    }
  };

  const totalLeads = stages.reduce((acc, stage) => acc + stage.leads.length, 0);
  const enrolledCount = stages.find((s) => s.id === "enrolled")?.leads.length || 0;
  const activeLeads = totalLeads - enrolledCount - (stages.find((s) => s.id === "lost")?.leads.length || 0);

  const handleResetFilters = () => {
    setSearchQuery("");
    setSelectedCourse("all");
    setSelectedSource("all");
    setSelectedPriority("all");
    setSelectedCounselor("all");
    toast.info("Filters reset to global view");
  };

  const handleEditLead = (lead: Lead) => {
    setEditingLead(lead); // open modal with this lead
  };


  return (
    <div className="h-full flex flex-col bg-slate-50 dark:bg-background">
      {/* Top Stats Bar */}
      <div className="bg-white dark:bg-card border-b border-slate-200 dark:border-slate-800 px-6 py-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-blue-100 dark:bg-blue-950 flex items-center justify-center">
              <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-slate-900 dark:text-white">{totalLeads}</p>
              <p className="text-xs text-slate-600 dark:text-slate-400">Total Leads</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-emerald-100 dark:bg-emerald-950 flex items-center justify-center">
              <TrendingUp className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-slate-900 dark:text-white">{activeLeads}</p>
              <p className="text-xs text-slate-600 dark:text-slate-400">Active Leads</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-green-100 dark:bg-green-950 flex items-center justify-center">
              <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-slate-900 dark:text-white">{enrolledCount}</p>
              <p className="text-xs text-slate-600 dark:text-slate-400">Enrolled</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-blue-100 dark:bg-blue-950 flex items-center justify-center">
              <ArrowUpRight className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-slate-900 dark:text-white">
                {totalLeads > 0 ? Math.round((enrolledCount / totalLeads) * 100) : 0}%
              </p>
              <p className="text-xs text-slate-600 dark:text-slate-400">Conversion Rate</p>
            </div>
          </div>
        </div>
      </div>

      {/* Action Bar */}
      <div className="bg-white dark:bg-card border-b border-slate-200 dark:border-slate-800 px-6 py-4">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          {/* Left Actions */}
          <div className="flex flex-wrap items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Add New Lead
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48 rounded-xl shadow-xl bg-white dark:bg-card border-slate-200 dark:border-slate-800">
                <DropdownMenuItem
                  onSelect={(e) => {
                    // Let the dropdown close naturally first to avoid focus-trap conflicts
                    // setIsAddLeadOpen will be called after the dropdown is dismissed
                    setTimeout(() => setIsAddLeadOpen(true), 150);
                  }}
                  className="gap-2 cursor-pointer focus:bg-blue-50 dark:focus:bg-blue-900/20"
                >
                  <UserPlus size={16} />
                  Add Single Lead
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={(e) => {
                    // Let the dropdown close naturally first to avoid focus-trap conflicts
                    setTimeout(() => setIsBulkImportOpen(true), 150);
                  }}
                  className="gap-2 cursor-pointer focus:bg-emerald-50 dark:focus:bg-emerald-900/20"
                >
                  <FileText size={16} />
                  Add Bulk Leads
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Dialog open={isAddLeadOpen} onOpenChange={setIsAddLeadOpen}>
              <DialogContent className="sm:max-w-[550px] bg-white dark:bg-card text-black dark:text-white rounded-lg shadow-lg p-6 border-slate-200 dark:border-slate-800">
                <DialogHeader>
                  <DialogTitle>Add New Leads</DialogTitle>
                  <DialogDescription>
                    Enter the details of the prospective student
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <Input id="name" placeholder="Enter full name" value={newLeadName} onChange={(e) => setNewLeadName(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input id="phone" placeholder="+91 98765 43210" value={newLeadPhone} onChange={(e) => setNewLeadPhone(e.target.value)} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input id="email" type="email" placeholder="student@email.com" value={newLeadEmail} onChange={(e) => setNewLeadEmail(e.target.value)} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="course">Course *</Label>
                      <Select
                        value={newLeadCourse}
                        onValueChange={setNewLeadCourse}
                      >
                        <SelectTrigger className="w-full rounded-lg bg-white dark:bg-background text-black dark:text-white border border-gray-300 dark:border-gray-700">
                          <SelectValue placeholder="Select course" />
                        </SelectTrigger>
                        <SelectContent className="bg-white dark:bg-card text-black dark:text-white rounded-lg shadow-md border-slate-200 dark:border-slate-800">
                          <SelectItem
                            value="btech-cse"
                            className="rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            B.Tech CSE
                          </SelectItem>
                          <SelectItem
                            value="btech-ece"
                            className="rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            B.Tech ECE
                          </SelectItem>
                          <SelectItem
                            value="mba"
                            className="rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            MBA
                          </SelectItem>
                          <SelectItem
                            value="mca"
                            className="rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            MCA
                          </SelectItem>
                          <SelectItem
                            value="bba"
                            className="rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            BBA
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="source">Lead Source*</Label>
                      <Select
                        value={newLeadSource}
                        onValueChange={(val: any) => setNewLeadSource(val)}
                      >
                        <SelectTrigger className="w-full rounded-lg bg-white dark:bg-background text-black dark:text-white border border-gray-300 dark:border-gray-700">
                          <SelectValue placeholder="Select source" />
                        </SelectTrigger>
                        <SelectContent className="bg-white dark:bg-[#0f172a] text-black dark:text-white rounded-lg shadow-md border-slate-200 dark:border-slate-800">
                          <SelectItem
                            value="Website"
                            className="rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            Website
                          </SelectItem>
                          <SelectItem
                            value="Walk-in"
                            className="rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            Walk-in
                          </SelectItem>
                          <SelectItem
                            value="Campaign"
                            className="rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            Campaign
                          </SelectItem>
                          <SelectItem
                            value="Referral"
                            className="rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            Referral
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="priority">Priority</Label>
                      <Select
                        value={newLeadPriority}
                        onValueChange={(val: any) => setNewLeadPriority(val)}
                      >
                        <SelectTrigger className="w-full rounded-lg bg-white dark:bg-background text-black dark:text-white border border-gray-300 dark:border-gray-700">
                          <SelectValue placeholder="Select priority" />
                        </SelectTrigger>
                        <SelectContent className="bg-white dark:bg-card text-black dark:text-white rounded-lg shadow-md border-slate-200 dark:border-slate-800">
                          <SelectItem
                            value="High"
                            className="rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            High
                          </SelectItem>
                          <SelectItem
                            value="Medium"
                            className="rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            Medium
                          </SelectItem>
                          <SelectItem
                            value="Low"
                            className="rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            Low
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="counselor">Assign Counselor</Label>
                      <Select
                        value={newLeadCounselor}
                        onValueChange={setNewLeadCounselor}
                      >
                        <SelectTrigger className="w-full rounded-lg bg-white dark:bg-background text-black dark:text-white border border-gray-300 dark:border-gray-700">
                          <SelectValue placeholder="Select counselor" />
                        </SelectTrigger>
                        <SelectContent className="bg-white dark:bg-[#0f172a] text-black dark:text-white rounded-lg shadow-md border-slate-200 dark:border-slate-800">
                          {/* Counselors should be fetched from API */}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes (Optional)</Label>
                    <Textarea id="notes" placeholder="Add any additional notes..." rows={3} value={newLeadNotes} onChange={(e) => setNewLeadNotes(e.target.value)} />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsAddLeadOpen(false)}>
                    Cancel
                  </Button>
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleAddLead}>
                    Add Lead
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Button variant="outline" className="rounded-lg" onClick={handleExport}>
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <Button variant="outline" className="rounded-lg" onClick={() => {
              const appCount = stages.find(s => s.id === "application")?.leads.length || 0;
              toast.info(`${appCount} applications in the pipeline`);
            }}>
              <UserPlus className="h-4 w-4 mr-2" />
              Applications
            </Button>
          </div>

          {/* Right Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search leads..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-64 rounded-lg bg-slate-50 dark:bg-background border-slate-200 dark:border-slate-800"
              />
            </div>
            <Select
              value={selectedCourse === "all" ? undefined : selectedCourse}
              onValueChange={setSelectedCourse}>
              <SelectTrigger className="w-40 rounded-lg bg-white dark:bg-background text-black dark:text-white border border-slate-200 dark:border-slate-800">
                <SelectValue placeholder="Select Course" />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-card text-black dark:text-white rounded-lg shadow-md border-slate-200 dark:border-slate-800">
                <SelectItem
                  value="all"
                  className="rounded-md hover:bg-gray-100 dark:hover:bg-gray-700">
                  All Courses
                </SelectItem>
                <SelectItem
                  value="btech-cse"
                  className="rounded-md hover:bg-gray-100 dark:hover:bg-gray-700">
                  B.Tech CSE
                </SelectItem>
                <SelectItem
                  value="mba"
                  className="rounded-md hover:bg-gray-100 dark:hover:bg-gray-700">
                  MBA
                </SelectItem>
                <SelectItem
                  value="bca"
                  className="rounded-md hover:bg-gray-100 dark:hover:bg-gray-700">
                  BCA
                </SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={selectedSource}
              onValueChange={setSelectedSource}
            >
              <SelectTrigger className="w-40 rounded-lg bg-white dark:bg-card text-black dark:text-white border border-slate-200 dark:border-slate-800">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-card text-black dark:text-white border border-slate-200 dark:border-slate-800">
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="website">Website</SelectItem>
                <SelectItem value="walk-in">Walk-in</SelectItem>
                <SelectItem value="campaign">Campaign</SelectItem>
                <SelectItem value="referral">Referral</SelectItem>
              </SelectContent>
            </Select>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant={selectedPriority !== "all" || selectedCounselor !== "all" ? "default" : "outline"} size="icon" className="rounded-lg relative">
                  <SlidersHorizontal className="h-4 w-4" />
                  {(selectedPriority !== "all" || selectedCounselor !== "all") && (
                    <span className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full border-2 border-white dark:border-slate-900" />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-6 bg-white dark:bg-card border-slate-200 dark:border-slate-800 shadow-2xl rounded-xl">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-bold text-sm uppercase tracking-widest text-foreground">Advanced Filters</h4>
                    <Button variant="ghost" size="sm" onClick={handleResetFilters} className="h-7 text-[10px] font-bold uppercase text-primary hover:bg-primary/5">
                      Reset
                    </Button>
                  </div>
                  <Separator className="bg-slate-100 dark:bg-slate-800" />
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-[10px] font-bold uppercase tracking-tight text-muted-foreground">Priority Level</Label>
                      <Select value={selectedPriority} onValueChange={setSelectedPriority}>
                        <SelectTrigger className="rounded-lg bg-slate-50 dark:bg-background border-slate-200 dark:border-slate-800 h-9 text-xs">
                          <SelectValue placeholder="All Priorities" />
                        </SelectTrigger>
                        <SelectContent className="bg-white dark:bg-card border-slate-200 dark:border-slate-800">
                          <SelectItem value="all">All Priorities</SelectItem>
                          <SelectItem value="High">High Priority</SelectItem>
                          <SelectItem value="Medium">Medium Priority</SelectItem>
                          <SelectItem value="Low">Low Priority</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-[10px] font-bold uppercase tracking-tight text-muted-foreground">Lead Counselor</Label>
                      <Select value={selectedCounselor} onValueChange={setSelectedCounselor}>
                        <SelectTrigger className="rounded-lg bg-slate-50 dark:bg-background border-slate-200 dark:border-slate-800 h-9 text-xs">
                          <SelectValue placeholder="All Counselors" />
                        </SelectTrigger>
                        <SelectContent className="bg-white dark:bg-card border-slate-200 dark:border-slate-800">
                          <SelectItem value="all">All Counselors</SelectItem>
                          {uniqueCounselors.map(name => (
                            <SelectItem key={name} value={name}>{name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="pt-2">
                    <p className="text-[9px] text-muted-foreground italic leading-tight">
                      Filters are applied in real-time to the Kanban columns.
                    </p>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden">
        <div className="h-full p-6">
          <div className="flex gap-4 h-full min-w-max">
            {filteredStages.map((stage) => (
              <div
                key={stage.id}
                className="flex-shrink-0 w-80 flex flex-col"
                onDragOver={handleDragOver}
                onDrop={() => handleDrop(stage.id)}
              >
                {/* Stage Header */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={cn("h-2 w-2 rounded-full", stage.color)} />
                      <h3 className="font-semibold text-slate-900 dark:text-white">
                        {stage.name}
                      </h3>
                    </div>
                    <Badge
                      className={cn(
                        "rounded-full text-white",
                        stage.color
                      )}
                    >
                      {stage.leads.length}
                    </Badge>

                  </div>
                  <div className="h-1 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div className={cn("h-full", stage.color)} style={{ width: "100%" }} />
                  </div>
                </div>

                {/* Lead Cards */}
                <div className="flex-1 overflow-y-auto space-y-3 pb-4">
                  {stage.leads.length === 0 ? (
                    <div className="h-32 flex flex-col items-center justify-center text-slate-400 dark:text-slate-600 text-sm border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-lg">
                      <Users className="h-8 w-8 mb-2 opacity-20" />
                      No records found
                    </div>
                  ) : (
                    <>
                      {stage.leads.slice(0, 50).map((lead) => (
                        <Card
                          key={lead.id}
                          draggable
                          onDragStart={() => handleDragStart(lead)}
                          className="cursor-move hover:shadow-lg transition-all duration-200 bg-white dark:bg-card border-slate-200 dark:border-slate-800"
                        >
                          <CardContent className="p-4 space-y-3">
                            {/* Lead Header */}
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1">
                                <h4 className="font-semibold text-slate-900 dark:text-white mb-1">
                                  {lead.name}
                                </h4>
                                <Badge className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400 hover:bg-blue-100">
                                  {lead.course}
                                </Badge>
                              </div>
                              <Badge className={cn("text-xs", getPriorityColor(lead.priority))}>
                                {lead.priority}
                              </Badge>
                            </div>

                            {/* Contact Info */}
                            <div className="space-y-2 text-sm">
                              <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                                <Phone className="h-3.5 w-3.5" />
                                <span className="text-xs">{lead.phone}</span>
                              </div>
                              <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                                <Mail className="h-3.5 w-3.5" />
                                <span className="text-xs truncate">{lead.email}</span>
                              </div>
                            </div>

                            {/* Lead Source */}
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className={cn("text-xs", getSourceColor(lead.source))}>
                                {lead.source}
                              </Badge>
                            </div>

                            {/* Counselor */}
                            <div className="flex items-center gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                              <Avatar className="h-6 w-6">
                                <AvatarImage src={lead.counselor.avatar} />
                                <AvatarFallback>{lead.counselor.name.charAt(0)}</AvatarFallback>
                              </Avatar>
                              <span className="text-xs text-slate-600 dark:text-slate-400 flex-1 truncate" title={lead.counselor.name}>
                                {lead.counselor.name}
                              </span>
                            </div>

                            {/* Last Follow-up */}
                            <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-500">
                              <Clock className="h-3 w-3" />
                              <span>Last: {lead.lastFollowUp}</span>
                            </div>

                            {/* Quick Actions */}
                            <div className="flex items-center gap-1 pt-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="flex-1 h-8 rounded-lg text-xs hover:bg-blue-50 dark:hover:bg-blue-950 hover:text-blue-600 dark:hover:text-blue-400"
                                onClick={() => {
                                  setSelectedLead(lead);
                                  setIsCallDialogOpen(true);
                                }}
                              >
                                <Phone className="h-3 w-3 mr-1" />
                                Call
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="flex-1 h-8 rounded-lg text-xs hover:bg-emerald-50 dark:hover:bg-emerald-950 hover:text-emerald-600 dark:hover:text-emerald-400"
                                onClick={() => {
                                  setSelectedLead(lead);
                                  setIsMessageDialogOpen(true);
                                }}
                              >
                                <MessageSquare className="h-3 w-3 mr-1" />
                                Message
                              </Button>
                              <Button onClick={() => handleEditLead(lead)}
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800">
                                <Edit className="h-3 w-3" />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                      {stage.leads.length > 50 && (
                        <div className="py-2 text-center">
                          <p className="text-xs text-slate-500">
                            Showing 50 of {stage.leads.length} leads.
                            Use search or filters to find specific records.
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Call Dialog */}
      <Dialog open={isCallDialogOpen} onOpenChange={setIsCallDialogOpen}>
        <DialogContent className="sm:max-w-[550px] bg-white dark:bg-card text-black dark:text-white rounded-lg shadow-lg p-6 border-slate-200 dark:border-slate-800">
          <DialogHeader>
            <DialogTitle>Call {selectedLead?.name}</DialogTitle>
            <DialogDescription>
              Make a call to {selectedLead?.name} at {selectedLead?.phone}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input id="phone" placeholder="+91 98765 43210" value={selectedLead?.phone} readOnly />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCallDialogOpen(false)}>
              Cancel
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => {
              toast.success(`Call to ${selectedLead?.name} initiated`);
              setIsCallDialogOpen(false);
            }}>
              Call
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LeadEditModal
        open={!!editingLead}
        onOpenChange={(open) => !open && setEditingLead(null)}
        lead={editingLead}
        type="general"
        onSuccess={fetchLeads}
      />

      {/* Message Dialog */}
      <Dialog open={isMessageDialogOpen} onOpenChange={setIsMessageDialogOpen}>
        <DialogContent className="sm:max-w-[550px] bg-white dark:bg-card text-black dark:text-white rounded-lg shadow-lg p-6 border-slate-200 dark:border-slate-800">
          <DialogHeader>
            <DialogTitle>Message {selectedLead?.name}</DialogTitle>
            <DialogDescription>
              Send a message to {selectedLead?.name} at {selectedLead?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input id="email" type="email" placeholder="student@email.com" value={selectedLead?.email} readOnly />
            </div>
            <div className="space-y-2">
              <Label htmlFor="message">Message</Label>
              <Textarea id="message" placeholder="Type your message here..." rows={3} value={messageText} onChange={(e) => setMessageText(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsMessageDialogOpen(false)}>
              Cancel
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => {
              toast.success(`Message sent to ${selectedLead?.name}`);
              setIsMessageDialogOpen(false);
            }}>
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CSVImportGeneralLeadModal
        open={isBulkImportOpen}
        onOpenChange={setIsBulkImportOpen}
        onImportSuccess={fetchLeads}
      />
    </div>
  );
}
